// src/api/finance/CatalogueApi.ts
import { ENV } from "../../config/runtimeEnv";
import {
	CatalogueAppointmentTypesParams,
	CatalogueAppointmentTypesResponse,
	CatalogueProductsParams,
	CatalogueProductsResponse,
	EMPTY_CATALOGUE_PAGINATION,
} from "../../types/finance/CatalogueTypes";
import { http } from "../../lib/http/client";

/**
 * The two catalogue reads behind the invoice line-item picker (finance S7).
 *
 * Same base URL and the same bearer header as `FinanceApi.ts` — the picker calls FINANCE, never
 * commerce or healthcare directly (parent decision A1). That is not a routing preference:
 * `api/inventory`'s `fetchProducts` still requires a `company_id` sourced from a column dropped in
 * July 2026, healthcare's public appointment-type index makes one outbound OAuth call PER TYPE, and
 * finance has to read both catalogues server-side anyway for S9's price snapshot. One proxy means
 * the picker and the server-side validation see identical data.
 *
 * ⚠️ **The fetchers are named `fetchCatalogue…` on purpose.** There are already TWO unrelated
 * `fetchAppointmentTypes` in this codebase with different signatures — `api/healthcare/AppointmentSettingsApi.ts`
 * and `api/healthcare/AppointmentApi.ts`. Neither is this; do not import either here, and do not
 * create a third collision.
 */

// Same base URL as the rest of the finance API — the `/api` segment is already part of it.
const FINANCE_BASE_URL = ENV.VITE_FINANCE_SERVER || "/api/finance";

const getHeaders = () => ({
	"Content-Type": "application/json",
});

/**
 * ⚠️ **The query keys are snake_case, and that is load-bearing.** Finance's global `ValidationPipe`
 * runs with `forbidNonWhitelisted: true`, so sending `perPage` is a **400**, not a silently ignored
 * key. `FinanceApi.ts` already builds `params: { page, per_page: perPage }` for every paginated
 * finance call; this follows it.
 *
 * Only keys that are actually set are sent, for the same reason `fetchInvoices` filters: an
 * explicit `undefined`/`""` reaching a validated param is a 400 rather than "no filter".
 *
 * `per_page` above `CATALOGUE_MAX_PER_PAGE` is deliberately NOT clamped here — finance answers a
 * 400 naming the field, which is the only one of the available outcomes a caller can act on.
 */
const buildProductQuery = (params: CatalogueProductsParams): Record<string, string | number> => {
	const query: Record<string, string | number> = {};

	const search = (params.search ?? "").trim();
	if (search) {
		query.search = search;
	}
	if (params.page !== undefined && params.page !== null) {
		query.page = params.page;
	}
	if (params.perPage !== undefined && params.perPage !== null) {
		query.per_page = params.perPage;
	}

	return query;
};

/**
 * One page of the product catalogue, prices already in integer cents.
 *
 * ⚠️ **Returns the WHOLE envelope, not just `data`.** `unavailable` is the difference between
 * "commerce is temporarily down" and "this clinic has no products", and dropping it here would
 * leave the picker unable to tell a practitioner which one they are looking at. The response is a
 * 200 in both cases — losing the picker must never cost the invoice.
 */
export const fetchCatalogueProducts = async (
	params: CatalogueProductsParams = {},
): Promise<CatalogueProductsResponse> => {
	const response = await http.get<CatalogueProductsResponse & { success?: boolean }>(
		`${FINANCE_BASE_URL}/catalogue/products`,
		{ headers: getHeaders(), params: buildProductQuery(params) },
	);

	return {
		data: response.data?.data ?? [],
		pagination: response.data?.pagination ?? EMPTY_CATALOGUE_PAGINATION,
		unavailable: response.data?.unavailable === true ? true : undefined,
	};
};

/**
 * The appointment-type catalogue, prices already in integer cents.
 *
 * `practitionerId` omitted ⇒ no `custom_price` override is resolved and every `unitPriceCents` is
 * the type's `default_price` (decision D7). Finance caches a successful list for 60s per
 * practitioner; a failure is never cached, so reopening the dropdown does reach healthcare again.
 *
 * ⚠️ **Also returns the whole envelope** — see `fetchCatalogueProducts`. The S11 brief typed this
 * one as `Promise<CatalogueAppointmentType[]>`, which throws the degrade flag away; it returns the
 * envelope for the same reason the products fetcher does.
 */
export const fetchCatalogueAppointmentTypes = async (
	params: CatalogueAppointmentTypesParams = {},
): Promise<CatalogueAppointmentTypesResponse> => {
	const query: Record<string, string> = {};
	if (params.practitionerId !== undefined && params.practitionerId !== null) {
		query.practitioner_id = params.practitionerId;
	}

	const response = await http.get<CatalogueAppointmentTypesResponse & { success?: boolean }>(
		`${FINANCE_BASE_URL}/catalogue/appointment-types`,
		{ headers: getHeaders(), params: query },
	);

	return {
		data: response.data?.data ?? [],
		unavailable: response.data?.unavailable === true ? true : undefined,
	};
};
